import { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { auth } from "../lib/firebase";
import { API_BASE_URL, authenticatedFetch, trackBackendEvent, fetchWithTimeout } from "../lib/api";
import { Bank, CustomSite, Category, CategorySite, MyCustomSite, SubscriptionStatus, CatalogSite, SiteOverride, LoadStatus } from "../types";
import { applyTierOverridesToCategories, withProtectableSites } from "../lib/siteOverrides";
import { SelectionAnswer, shownSelection } from "../lib/sitePinning";
import { protectedModelOf } from "../lib/protectedModel";

/**
 * ENG-3156: what `/my-categories` says about the caller's org site-tier overrides. `promotedSites`
 * are the identity-only catalog rows the org (or the caller's groups) promoted to Protected, with
 * the metadata the page needs to list them; `siteOverrides` are the resolved overrides themselves,
 * so demotions can hide a row. A backend that predates this sends neither, which reads as "no
 * overrides" — exactly the old behaviour.
 */
interface MyCategoriesOverrides {
  siteOverrides: SiteOverride[];
  promotedSites: CatalogSite[];
}
const readOverrides = (data: { siteOverrides?: SiteOverride[]; promotedSites?: CatalogSite[] }): MyCategoriesOverrides => ({
  siteOverrides: Array.isArray(data.siteOverrides) ? data.siteOverrides : [],
  promotedSites: Array.isArray(data.promotedSites) ? data.promotedSites : [],
});
const NO_OVERRIDES: MyCategoriesOverrides = { siteOverrides: [], promotedSites: [] };

/** The selection in a `/my-categories` read or a save's answer. The server omits an empty list. */
type SelectionFields = { categories?: string[]; enabledSites?: string[]; disabledSites?: string[] };
const listOrEmpty = (value: string[] | undefined): string[] => (Array.isArray(value) ? value : []);
const readAnswer = (data: SelectionFields): SelectionAnswer => ({
  categories: listOrEmpty(data.categories),
  enabledSites: listOrEmpty(data.enabledSites),
  disabledSites: listOrEmpty(data.disabledSites),
});

/**
 * ENG-3621: whether two `/my-categories` reads say the same thing. Both are small JSON trees parsed
 * from the server's answer, and the server lists them in a stable order, so their serializations are
 * a cheap structural comparison; an answer that only reorders reads as a change, which costs one
 * render, never a missed one.
 */
const sameJson = (a: unknown, b: unknown): boolean => JSON.stringify(a) === JSON.stringify(b);

/** A view built from several loads: failed if any failed, loaded once all have (ENG-3342). */
const combinedStatus = (...parts: LoadStatus[]): LoadStatus =>
  parts.includes("error") ? "error" : parts.every((p) => p === "loaded") ? "loaded" : "loading";

/** A later refresh that fails keeps what an earlier one loaded; only a load with nothing to show is an error. */
const failedUnlessLoaded = (s: LoadStatus): LoadStatus => (s === "loaded" ? s : "error");
const loadingUnlessLoaded = (s: LoadStatus): LoadStatus => (s === "loaded" ? s : "loading");

/** ENG-3342: shown when a toggle's save fails — the control has been put back, so say so. */
const saveFailedMessage = (what: string | undefined) =>
  `Could not save your change to ${what ?? "your protected sites"}, so it has been switched back. Try again in a moment.`;

/**
 * ENG-4027 (!1889 review S-F): the backend's Protected model changed since this page read it, so a
 * whole-set write built under the old one was refused (409). Nothing was saved; the page reloads.
 */
const MODEL_CHANGED_MESSAGE =
  "Haven's Protected settings changed since this page loaded, so it has been reloaded and nothing was changed. Try again.";

/** ENG-4027: a Protected-model migration held the policy lock and the write gave up (503). */
const MIGRATION_RUNNING_MESSAGE =
  "Haven is updating Protected settings right now, so nothing was changed. Try again in a minute.";

/** Why a save did not land: a plain failure, the model changing under the page, or a migration. */
type SaveOutcome = "saved" | "failed" | "model_changed" | "migration_running";

const failureMessage = (outcome: Exclude<SaveOutcome, "saved">, what: string | undefined): string =>
  outcome === "model_changed" ? MODEL_CHANGED_MESSAGE
    : outcome === "migration_running" ? MIGRATION_RUNNING_MESSAGE
      : saveFailedMessage(what);

/**
 * ENG-4027: a 503 is a running migration when the body says so. PATCH answers JSON with
 * `code: "policy_migration_running"`; the whole-set PUT answers plain text (ENG-3367: the mobile apps
 * must not decode an error as a selection), so its fixed sentence is matched instead.
 */
const isMigrationRunning = async (response: Response): Promise<boolean> => {
  if (response.status !== 503) return false;
  const text = await response.text().catch(() => "");
  try {
    const body = JSON.parse(text) as { code?: string };
    if (body && body.code === "policy_migration_running") return true;
  } catch {
    // plain text: fall through
  }
  return /migration is running/i.test(text);
};

/** How often to poll for category/site changes from other team members (30 seconds) */
const CATEGORIES_POLL_INTERVAL_MS = 30 * 1000;

/** Feature flag: set to true to enable the categories UI for end users */
export const CATEGORIES_ENABLED = true;

export function useSecureSites(
  subscriptionStatus?: SubscriptionStatus | null,
  /**
   * ENG-4027: re-read `/subscription-status`, for when a save finds the Protected model changed under
   * the page (409). App passes useSubscription's fetch; absent in tests that do not need it.
   */
  refetchSubscription?: () => unknown,
) {
  // ENG-4027 (!1889 review S-F): every selection write names the model the page was built under. The
  // whole-set PUTs are refused (409) under model 2 without it, or when it does not match; the PATCHes
  // do not check it today, and carry it anyway so a later check finds it there.
  const protectedModel = protectedModelOf(subscriptionStatus?.protectedModel);
  // Tenant-enforced policy (ENG-2365), delivered on the subscription-status response.
  // LOCKED = admin forced-ON (the user cannot turn it off). The extension is the source of truth for
  // enforcement; here we mirror it so the UI can't offer a toggle the backend would reject.
  // ENG-4054: the site/category deny is retired and the backend sends it as `[]`, so it is not read.
  const lockedCategories = subscriptionStatus?.lockedCategories ?? [];
  const lockedSites = subscriptionStatus?.lockedSites ?? [];
  const [banks, setBanks] = useState<Bank[]>([]);

  const [customSites, setCustomSites] = useState<CustomSite[]>([]);
  const [loadingCustomSites, setLoadingCustomSites] = useState(false);
  const [customSiteError, setCustomSiteError] = useState<string | null>(null);

  // Personal custom protected sites for free/solo users (no team), backed by the
  // server-side /my-custom-sites endpoints (mirrors the team /business/custom-sites flow).
  const [myCustomSites, setMyCustomSites] = useState<MyCustomSite[]>([]);
  // ENG-3342: a failed /my-custom-sites used to leave the list empty, and the page said "No custom
  // sites yet" to someone who may have several.
  const [myCustomSitesStatus, setMyCustomSitesStatus] = useState<LoadStatus>("loading");
  const [addingMyCustomSite, setAddingMyCustomSite] = useState(false);
  const [myCustomSiteError, setMyCustomSiteError] = useState<string | null>(null);

  // Haven's catalog by category (`GET /categories`, public, protected tier only) …
  const [rawCategories, setCategories] = useState<Category[]>([]);
  // ENG-3342: a failed /categories rendered as an empty Categories section.
  const [catalogStatus, setCatalogStatus] = useState<LoadStatus>("loading");
  // … and the org's view of it (ENG-3156): promoted identity-only rows join their category, demoted
  // rows leave it. Same merge the org-admin Policy editor does, from the same helper.
  const [overrides, setOverrides] = useState<MyCategoriesOverrides>(NO_OVERRIDES);
  const categories = useMemo(
    // ENG-4033: under model 2 every category can be Protected, so none is dropped (withProtectableSites).
    // Read off the status directly: this hook runs in App, outside DashboardLayout's model provider.
    () => withProtectableSites(applyTierOverridesToCategories(rawCategories, overrides.promotedSites, overrides.siteOverrides), protectedModelOf(subscriptionStatus?.protectedModel)),
    [rawCategories, overrides, subscriptionStatus?.protectedModel],
  );
  // ENG-3161: the apps whose use is reported to the member's organization — Haven's `tracked`
  // default with the org's (and the member's groups') tracked overrides applied, by title. Shown on
  // the page so the member is told what the report covers, not only that it exists.
  const trackedApps = useMemo(() => {
    const overridden = new Map(
      overrides.siteOverrides.filter((o) => typeof o.tracked === "boolean").map((o) => [o.slug, o.tracked as boolean]),
    );
    return banks
      .filter((b) => (overridden.get(b.slug) ?? b.tracked) === true)
      .map((b) => b.name)
      .sort((a, b) => a.localeCompare(b));
  }, [banks, overrides]);
  // ENG-4033: the rows Haven lists as Verified only (`banks.verified`), by category slug, for the
  // Protected sites page under model 2: there an enabled category outside Haven's five makes every one
  // of its rows Protected, these included (docs/CATALOG_GLOSSARY.md §3.3 step 4), but `/categories`
  // does not list them. Inside the five they are staff exceptions an enabled category does not reach,
  // so they are left out. Read from the public `/banks`, which the page already loads.
  // ENG-4058: the same rows inside the five, in their own map (`defaultVerifiedRowsByCategory`): a lock
  // or an addition makes them Protected under model 2, and the page lists and counts those.
  const [verifiedRowsByCategory, defaultVerifiedRowsByCategory] = useMemo(() => {
    const slugByName = new Map(rawCategories.map((c) => [c.name, c]));
    const outside = new Map<string, CategorySite[]>();
    const inside = new Map<string, CategorySite[]>();
    for (const b of banks) {
      if (!b.verified || b.hidden) continue;
      const cat = slugByName.get(b.category ?? 'Banking & Wealth');
      if (!cat) continue;
      const out = cat.shieldedByDefault ? inside : outside;
      const row: CategorySite = { slug: b.slug, title: b.name, launchUrl: b.launchUrl ?? '', clearbitLogoKey: b.clearbitLogoKey ?? null };
      out.set(cat.slug, [...(out.get(cat.slug) ?? []), row]);
    }
    for (const out of [outside, inside]) out.forEach((rows) => rows.sort((a, b) => a.title.localeCompare(b.title)));
    return [outside, inside] as const;
  }, [banks, rawCategories]);
  const [loadingCategories, setLoadingCategories] = useState(false);
  // ENG-3342: null until /my-categories has answered — unknown, not a default. The selection started
  // as ["banking-wealth"] and [] and stayed that way when the load failed, and a toggle then PUT that
  // default plus one change. The server takes the PUT as the WHOLE set and removes the sites of
  // every category it does not name, so one click after a failed load wiped the saved selection —
  // org-wide, for an owner or admin. Nothing below may build a save from a null. (ENG-3417: saves
  // now send one change, but which way a click goes is still read from this.)
  // ENG-3367: this is the server's answer. What the page shows, and what a save is built from, is
  // `selection` below, because an answer with no categories stands for the extension's default.
  const [answer, setAnswer] = useState<SelectionAnswer | null>(null);
  const selection = useMemo(
    () => (answer ? shownSelection(answer, catalogStatus === "loaded" ? categories : null) : null),
    [answer, catalogStatus, categories],
  );
  const enabledCategories = selection?.categories ?? null;
  const enabledSites = selection?.sites ?? null;
  const [selectionFailed, setSelectionFailed] = useState(false);
  // ENG-3367: the selection is unknown because a save went through and has not been read back yet
  // (see applySavedAnswer). If that read fails, the page must not say "Nothing has been changed".
  const [savedButNotReloaded, setSavedButNotReloaded] = useState(false);
  const [loadingEnabledCategories, setLoadingEnabledCategories] = useState(false);
  const [expandedCategory, setExpandedCategory] = useState<string | null>(null);
  // Saves still waiting on the server. A poll that lands while one is out would put back the
  // pre-save selection and flicker the click undone, so reads are not applied until the saves have
  // answered. The generation catches the other ordering: a read sent before a save started can
  // answer after it finished.
  const savesInFlight = useRef(0);
  const saveGeneration = useRef(0);
  // ENG-3417: set when saves overlapped (or one failed), so the page re-reads once they are all in.
  // Each save's answer is the selection as of that save, and with two out the first to answer does
  // not have the other's change, so neither answer is the page's to show.
  const rereadAfterSaves = useRef(false);

  // Loaded once the server has answered. `selection` can still be null then (a default waiting on the
  // catalog), and `categoriesStatus` waits for the catalog too.
  const selectionStatus: LoadStatus = answer != null ? "loaded" : selectionFailed ? "error" : "loading";
  /** The Categories list needs Haven's catalog and this browser's selection both. */
  const categoriesStatus = combinedStatus(catalogStatus, selectionStatus);

  const sendToExtension = (
    message: Record<string, any>,
    getExtensionId: () => string | null
  ): Promise<any> => {
    const extId = getExtensionId();
    if (!extId || typeof chrome === 'undefined' || !chrome.runtime) {
      return Promise.reject(new Error("Haven extension not detected. Please install the extension first."));
    }
    return new Promise((resolve, reject) => {
      chrome.runtime.sendMessage(extId, message, (response) => {
        if (chrome.runtime.lastError) {
          reject(new Error(chrome.runtime.lastError.message || "Extension not available"));
        } else {
          resolve(response);
        }
      });
    });
  };

  const [loadingBanks, setLoadingBanks] = useState(false);
  // ENG-3621: true from the start of a /banks load until one fails, so `loadBanksOnce` asks once per
  // session and a failed load is asked again on the next visit.
  const banksRequested = useRef(false);

  const fetchBanks = async () => {
    banksRequested.current = true;
    setLoadingBanks(true);
    try {
      const response = await fetchWithTimeout(`${API_BASE_URL}/banks`);
      if (!response.ok) throw new Error(`/banks answered ${response.status}`);
      const data = await response.json();
      setBanks(data.map((b: any) => ({
        slug: b.slug,
        name: b.title || b.name || b.slug,
        logoUrl: b.clearbitLogoKey ? `https://logo.clearbit.com/${b.clearbitLogoKey}` : (b.logoUrl || ''),
        tracked: b.tracked === true,
        category: typeof b.category === 'string' ? b.category : null,
        verified: b.verified === true,
        hidden: b.hidden === true,
        launchUrl: typeof b.launchUrl === 'string' ? b.launchUrl : '',
        clearbitLogoKey: b.clearbitLogoKey ?? null,
      })));
    } catch (err) {
      console.error('Error fetching banks:', err);
      banksRequested.current = false;
    } finally {
      setLoadingBanks(false);
    }
  };

  /**
   * ENG-3621: the whole catalog, for `trackedApps` alone, which only Protected sites shows. It was
   * fetched at every sign-in; App now asks for it when that page opens, and only the first time.
   */
  const loadBanksOnce = () => {
    if (!banksRequested.current) void fetchBanks();
  };

  const fetchCustomSites = async (getExtensionId: () => string | null) => {
    setLoadingCustomSites(true);
    setCustomSiteError(null);
    try {
      const response = await sendToExtension({ action: "get-custom-sites" }, getExtensionId);
      if (response?.success) {
        setCustomSites(response.sites || []);
      }
    } catch (err: unknown) {
      console.log("Could not fetch custom sites:", err instanceof Error ? err.message : String(err));
      setCustomSiteError("Install the Haven extension to manage custom sites.");
    } finally {
      setLoadingCustomSites(false);
    }
  };

  const handleRemoveCustomSite = async (
    siteId: string,
    getExtensionId: () => string | null
  ) => {
    setCustomSiteError(null);
    try {
      const response = await sendToExtension({
        action: "remove-custom-site",
        siteId,
      }, getExtensionId);
      if (response?.success) {
        setCustomSites(response.sites || []);
      }
    } catch (err: unknown) {
      setCustomSiteError(err instanceof Error ? err.message : "Failed to communicate with extension");
    }
  };

  // ── Personal custom protected sites (no team) — server-backed via /my-custom-sites ──
  const fetchMyCustomSites = async () => {
    if (!auth.currentUser) return;
    setMyCustomSitesStatus(loadingUnlessLoaded);
    try {
      const response = await authenticatedFetch('/my-custom-sites');
      if (!response.ok) throw new Error(`/my-custom-sites answered ${response.status}`);
      setMyCustomSites(await response.json());
      setMyCustomSitesStatus("loaded");
    } catch (err) {
      console.error('Error fetching personal custom sites:', err);
      setMyCustomSitesStatus(failedUnlessLoaded);
    }
  };

  const handleAddMyCustomSite = async (
    name: string,
    url: string,
    getExtensionId?: () => string | null
  ): Promise<{ success: boolean; error?: string }> => {
    if (!name.trim() || !url.trim()) return { success: false };
    setAddingMyCustomSite(true);
    setMyCustomSiteError(null);
    try {
      const response = await authenticatedFetch('/my-custom-sites', {
        method: 'POST',
        body: JSON.stringify({ title: name.trim(), launchUrl: url.trim() }),
      });
      const data = await response.json();
      if (response.ok) {
        fetchMyCustomSites();
        trackBackendEvent("protected_sites_changed", { change_type: "custom_site_added", surface: "account" });
        // Notify extension to re-fetch server-side custom sites and update protection
        if (getExtensionId) {
          sendToExtension({ action: "custom-sites-updated" }, getExtensionId).catch(() => {});
        }
        return { success: true };
      } else {
        const errorMsg = data.error || 'Failed to add site';
        setMyCustomSiteError(errorMsg);
        return { success: false, error: errorMsg };
      }
    } catch (err: unknown) {
      const errorMsg = err instanceof Error ? err.message : 'Failed to add site';
      setMyCustomSiteError(errorMsg);
      return { success: false, error: errorMsg };
    } finally {
      setAddingMyCustomSite(false);
    }
  };

  const handleRemoveMyCustomSite = async (
    siteId: string,
    getExtensionId?: () => string | null
  ): Promise<{ success: boolean }> => {
    try {
      const response = await authenticatedFetch(`/my-custom-sites/${siteId}`, {
        method: 'DELETE',
      });
      if (response.ok) {
        fetchMyCustomSites();
        trackBackendEvent("protected_sites_changed", { change_type: "custom_site_removed", surface: "account" });
        // Notify extension to re-fetch server-side custom sites and update protection
        if (getExtensionId) {
          sendToExtension({ action: "custom-sites-updated" }, getExtensionId).catch(() => {});
        }
        return { success: true };
      }
      return { success: false };
    } catch (err) {
      console.error('Error removing personal custom site:', err);
      return { success: false };
    }
  };

  const fetchCategories = async () => {
    if (!CATEGORIES_ENABLED) return;
    setLoadingCategories(true);
    setCatalogStatus(loadingUnlessLoaded);
    try {
      const response = await fetchWithTimeout(`${API_BASE_URL}/categories`);
      if (!response.ok) throw new Error(`/categories answered ${response.status}`);
      setCategories(await response.json());
      setCatalogStatus("loaded");
    } catch (err) {
      console.error('Error fetching categories:', err);
      setCatalogStatus(failedUnlessLoaded);
    } finally {
      setLoadingCategories(false);
    }
  };

  /**
   * One `/my-categories` answer, applied. Stable (it only calls setters), so the poll effect below
   * can depend on it without re-running. ENG-3621: an answer that says what the last one said keeps
   * the objects already held, so the 30s poll re-renders nothing (the catalog merge, the selection
   * and the page) when nothing changed, which is nearly every time.
   */
  const applySelection = useCallback((data: SelectionFields & {
    siteOverrides?: SiteOverride[];
    promotedSites?: CatalogSite[];
  }) => {
    const nextAnswer = readAnswer(data);
    const nextOverrides = readOverrides(data);
    setAnswer((prev) => (prev && sameJson(prev, nextAnswer) ? prev : nextAnswer));
    setOverrides((prev) => (sameJson(prev, nextOverrides) ? prev : nextOverrides));
    setSelectionFailed(false);
    setSavedButNotReloaded(false);
  }, []);

  /**
   * Re-read the selection in the background: the 30s poll, and the check after a failed save or
   * after saves that overlapped (see saveChange). Quiet by design — a failure leaves the state as it is and the next poll tries again —
   * and it yields to any save still in flight (see `savesInFlight`).
   */
  const refreshSelectionQuietly = useCallback(async () => {
    if (!auth.currentUser || !CATEGORIES_ENABLED) return;
    const generation = saveGeneration.current;
    try {
      // ENG-3031: unscoped, matching fetchEnabledCategories below.
      const response = await authenticatedFetch('/my-categories', {}, { scoped: false });
      if (!response.ok) return;
      const data = await response.json();
      if (savesInFlight.current === 0 && saveGeneration.current === generation) applySelection(data);
    } catch {
      // Silent: this is a background refresh. A selection that has never loaded keeps its error.
    }
  }, [applySelection]);

  const fetchEnabledCategories = async () => {
    if (!CATEGORIES_ENABLED) return;
    if (!auth.currentUser) return;
    setLoadingEnabledCategories(true);
    // Clears a previous failure, so a retry shows as loading rather than as the old error.
    setSelectionFailed(false);
    try {
      // ENG-3031: UNSCOPED on purpose. This page shows the protected sites applied to THIS browser,
      // which is what its cross-org banner (ENG-2682) already tells the reader — sending the
      // active-tenant header made the toggles the VIEWED org's while the banner named the governing
      // one, so the page contradicted its own notice. Editing a viewed org's policy is the Policy
      // page's job (locked/denied, ENG-2496), which the banner links to. No-op unless a tenant
      // switch is active: with no selection there is no header to suppress.
      const response = await authenticatedFetch('/my-categories', {}, { scoped: false });
      if (!response.ok) throw new Error(`/my-categories answered ${response.status}`);
      applySelection(await response.json());
    } catch (err) {
      // ENG-3342: said, not just logged. A selection that already loaded (the poll can get there
      // first) is unaffected — `selectionStatus` only reads the failure while there is no selection.
      console.error('Error fetching enabled categories:', err);
      setSelectionFailed(true);
    } finally {
      setLoadingEnabledCategories(false);
    }
  };

  /**
   * A save's answer (ENG-3367). The server reads an empty category list as its default: Banking &
   * Wealth for an org, every category for a personal account. An org's save is answered with that
   * default. A personal account's save that stores the empty list, like any save on a backend that
   * predates ENG-3367, is answered with the list itself, which says nothing about what is protected.
   * Only a read says, so the selection goes back to unknown until one answers: the page waits, and
   * nothing is saved from a guess. It is the page's own load, so a read that fails shows the load
   * error with Try again; a quiet one left the page loading for good. That error says the change
   * was saved (`savedButNotReloaded`), since here it was.
   */
  const applySavedAnswer = (data: SelectionFields) => {
    const next = readAnswer(data);
    if (next.categories.length > 0) {
      setAnswer(next);
      return;
    }
    setAnswer(null);
    setSavedButNotReloaded(true);
    void fetchEnabledCategories();
  };

  /** ENG-3342: the error states' "Try again" — re-runs whichever of this page's loads failed. */
  const retryFailedLoads = () => {
    if (catalogStatus === "error") void fetchCategories();
    if (selectionStatus === "error") void fetchEnabledCategories();
  };

  // Poll for category/site changes made by other team members. ENG-3621: App turns it on only while
  // a page that shows the selection is open (Protected sites, Policy); it used to run on every page.
  const pollingRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const [pollingEnabled, setPollingEnabled] = useState(false);

  const startCategoriesPolling = useCallback(() => {
    setPollingEnabled(true);
  }, []);

  const stopCategoriesPolling = useCallback(() => {
    setPollingEnabled(false);
  }, []);

  useEffect(() => {
    if (!pollingEnabled) {
      if (pollingRef.current) {
        clearInterval(pollingRef.current);
        pollingRef.current = null;
      }
      return;
    }

    // ENG-3342: a successful poll also recovers a selection whose first load failed — the page's
    // error clears and the toggles come back without the member having to press Try again.
    const pollOnce = refreshSelectionQuietly;

    // Only poll while the tab is actually being looked at. This runs every 30s for the lifetime of
    // every open account tab, and a backgrounded tab left open overnight was polling all night for a
    // list the user cannot see change. It is real load: /my-categories took 55 of the 400+ refusals
    // when the backend was saturated on 2026-07-30 (ENG-2637).
    const start = () => {
      if (pollingRef.current) return;
      pollingRef.current = setInterval(pollOnce, CATEGORIES_POLL_INTERVAL_MS);
    };
    const stop = () => {
      if (pollingRef.current) {
        clearInterval(pollingRef.current);
        pollingRef.current = null;
      }
    };
    // Poll immediately on becoming visible, so pausing never shows stale toggles: the extension may
    // have changed them while the tab was hidden, and waiting up to 30s to notice would be worse
    // than the polling this avoids.
    const onVisibility = () => {
      if (document.visibilityState === "visible") {
        void pollOnce();
        start();
      } else {
        stop();
      }
    };

    onVisibility();
    document.addEventListener("visibilitychange", onVisibility);

    return () => {
      document.removeEventListener("visibilitychange", onVisibility);
      stop();
    };
  }, [pollingEnabled, refreshSelectionQuietly]);

  /**
   * ENG-3417: send one change, `{slug, enabled}`, rather than the whole set. `PUT /my-categories` and
   * `PUT /my-sites` replace the whole selection with what this page last read, so two admins, or one
   * admin in two tabs, silently undid each other org-wide. The server applies a change to what it
   * holds, so both land. A change rather than a version check because each click here is one change
   * on its own: see the backend's SiteSelectionWrites.kt.
   *
   * [wholeSet] is the old request, for a backend that predates the PATCH routes (it answers 404 or
   * 405). It keeps the page working if this reaches production before the backend does, with the
   * old overwrite hazard for that window. Remove it once the backend with ENG-3417 is on prod.
   *
   * ENG-3031: unscoped, so a save writes the selection that governs this browser rather than the
   * viewed org's: the read and this write must target the same tenant, or the page would show one
   * org's toggles and persist them onto another's. Both routes resolve the tenant the same way on the
   * server (findCategoryTenantAndRole).
   */
  const sendChange = async (path: "/my-categories" | "/my-sites", slug: string, enabled: boolean, wholeSet: Record<string, string[]>) => {
    const response = await authenticatedFetch(path, {
      method: 'PATCH',
      body: JSON.stringify({ slug, enabled, protectedModel }),
    }, { scoped: false });
    if (response.status !== 404 && response.status !== 405) return response;
    return authenticatedFetch(path, { method: 'PUT', body: JSON.stringify({ ...wholeSet, protectedModel }) }, { scoped: false });
  };

  /**
   * Save one change and settle the page on the answer. True when it saved. A failed save puts the
   * selection back to [prevAnswer] and re-reads, since a request can fail after the server applied
   * it, or time out on our side after it succeeded on theirs.
   */
  const saveChange = async (
    path: "/my-categories" | "/my-sites",
    slug: string,
    enabled: boolean,
    wholeSet: Record<string, string[]>,
    prevAnswer: SelectionAnswer,
  ): Promise<SaveOutcome> => {
    savesInFlight.current += 1;
    saveGeneration.current += 1;
    let saved = false;
    let outcome: SaveOutcome = "failed";
    try {
      const response = await sendChange(path, slug, enabled, wholeSet);
      if (response.ok) {
        const data = await response.json();
        saved = true;
        outcome = "saved";
        if (savesInFlight.current === 1) applySavedAnswer(data);
        else rereadAfterSaves.current = true;
      } else if (response.status === 409) {
        // ENG-4027: the model changed under the page. Nothing was saved. The status is re-read so the
        // page words itself for the live model; the selection is re-read below, as for any failure.
        outcome = "model_changed";
        console.error(`Refused ${path}: the Protected model changed since this page loaded`);
        void refetchSubscription?.();
      } else if (await isMigrationRunning(response)) {
        // ENG-4027: once, no retry loop: the member is told to try again in a minute.
        outcome = "migration_running";
        console.error(`Refused ${path}: a Protected-model migration is running`);
      } else {
        const errorData = await response.json().catch(() => ({}));
        console.error(`Failed to save ${path}:`, errorData.error);
      }
    } catch (err) {
      console.error(`Error saving ${path}:`, err);
    } finally {
      savesInFlight.current -= 1;
    }
    if (!saved) {
      // Put back only when nothing else is out: another click's optimistic change is not this one's
      // to undo, and the re-read below settles both.
      if (savesInFlight.current === 0) setAnswer(prevAnswer);
      rereadAfterSaves.current = true;
    }
    if (savesInFlight.current === 0 && rereadAfterSaves.current) {
      rereadAfterSaves.current = false;
      void refreshSelectionQuietly();
    }
    return outcome;
  };

  /**
   * Toggle one category. Resolves to null when it saved (or there was nothing to do) and to a
   * sentence for the member when it did not — ENG-3342: a failed save used to revert the checkbox
   * with only a console.error, so the member saw their click undo itself and nothing else.
   */
  const toggleCategory = async (slug: string, getExtensionId?: () => string | null): Promise<string | null> => {
    // Admin-locked categories are not user-toggleable — ignore the attempt (ENG-2365).
    if (lockedCategories.includes(slug)) return null;

    // ENG-3342: which way the click goes (on or off) is read from the selection, so there is no
    // save until the server has given one. The page offers no toggle until then; this is the
    // guarantee behind that. ENG-3367: what the page shows, which for an org with nothing saved is
    // the default.
    if (answer == null || selection == null) {
      return "Your protected sites haven't loaded yet, so nothing was changed.";
    }
    const prevAnswer = answer;

    const wasEnabled = selection.categories.includes(slug);
    const newCategories = wasEnabled
      ? selection.categories.filter(s => s !== slug)
      : [...selection.categories, slug];

    // Optimistic update for sites: add/remove all sites in the toggled category
    const category = categories.find(c => c.slug === slug);
    const categorySiteSlugs = category ? category.sites.map(s => s.slug) : [];
    let newSites = selection.sites;
    if (categorySiteSlugs.length > 0) {
      if (wasEnabled) {
        // Removing category — remove its sites
        const toRemove = new Set(categorySiteSlugs);
        newSites = selection.sites.filter(s => !toRemove.has(s));
      } else {
        // Enabling category — add its sites
        const existing = new Set(selection.sites);
        newSites = [...selection.sites, ...categorySiteSlugs.filter(s => !existing.has(s))];
      }
    }
    // ENG-3367: no optimistic update for the last category. An empty list is the server's default,
    // not "nothing", and only its answer says which default; until then the page keeps showing what
    // is actually protected. toggleSite does the same for the last site (ENG-3417).
    if (newCategories.length > 0) {
      setAnswer({ categories: newCategories, enabledSites: newSites, disabledSites: prevAnswer.disabledSites });
    }

    const outcome = await saveChange('/my-categories', slug, !wasEnabled, { categories: newCategories }, prevAnswer);
    if (outcome === "saved") {
      // A saved change satisfies the EMAIL-04 "reviewed Protected Sites" skip (ENG-2136).
      trackBackendEvent("protected_sites_changed", { change_type: "category", surface: "account" });
      // Notify extension to refresh categories immediately
      if (getExtensionId) {
        sendToExtension({ action: "categories-updated" }, getExtensionId).catch(() => {});
      }
      return null;
    }
    return failureMessage(outcome, category?.name);
  };

  /** Toggle one site. Resolves like [toggleCategory]: null when saved, a sentence when not. */
  const toggleSite = async (siteSlug: string, getExtensionId?: () => string | null): Promise<string | null> => {
    // Admin-locked sites are not user-toggleable — ignore the attempt (ENG-2365).
    if (lockedSites.includes(siteSlug)) return null;

    // ENG-3342: same rule as toggleCategory.
    if (answer == null || selection == null) {
      return "Your protected sites haven't loaded yet, so nothing was changed.";
    }
    const prevAnswer = answer;

    // Optimistic update. ENG-3367: from what the page shows, so in an org with nothing saved the
    // default loses this one site. The optimistic answer names the categories shown as well: left
    // empty, the default would put the site straight back until the server answers.
    const wasEnabled = selection.sites.includes(siteSlug);
    const newSites = wasEnabled
      ? selection.sites.filter(s => s !== siteSlug)
      : [...selection.sites, siteSlug];
    // ENG-3417: no optimistic update for the last site, as for the last category. With no site on,
    // the server stores nothing, and nothing reads back as the default (every default site on, the
    // earlier opt-outs gone), so the org returns to the default. Showing every box unticked until the
    // answer lands, then all of them ticked again, was the snap-back. Whether the last one should be
    // refusable instead is an open product decision on ENG-3417, not settled here.
    if (newSites.length > 0) {
      setAnswer({ categories: selection.categories, enabledSites: newSites, disabledSites: prevAnswer.disabledSites });
    }

    const outcome = await saveChange('/my-sites', siteSlug, !wasEnabled, { sites: newSites }, prevAnswer);
    if (outcome === "saved") {
      // A saved change satisfies the EMAIL-04 "reviewed Protected Sites" skip (ENG-2136).
      trackBackendEvent("protected_sites_changed", { change_type: "site", surface: "account" });
      // Notify extension to refresh categories immediately
      if (getExtensionId) {
        sendToExtension({ action: "categories-updated" }, getExtensionId).catch(() => {});
      }
      return null;
    }
    const site = categories.flatMap(c => c.sites).find(s => s.slug === siteSlug);
    return failureMessage(outcome, site?.title);
  };

  /**
   * ENG-4063: turn one site of a selected category on or off, in the direction asked rather than by
   * flipping. For the protectedModel 2 member list, where a site is Protected while its category is
   * selected and it is not opted out (`disabledSites`), whatever `enabledSites` lists. Personal
   * accounts only: it writes the caller's own `/my-sites`, like toggleSite.
   */
  const setSiteEnabled = async (siteSlug: string, enabled: boolean, getExtensionId?: () => string | null): Promise<string | null> => {
    if (lockedSites.includes(siteSlug)) return null;
    if (answer == null || selection == null) {
      return "Your protected sites haven't loaded yet, so nothing was changed.";
    }
    const prevAnswer = answer;
    const sites = enabled
      ? Array.from(new Set([...selection.sites, siteSlug]))
      : selection.sites.filter((s) => s !== siteSlug);
    const disabled = enabled
      ? prevAnswer.disabledSites.filter((s) => s !== siteSlug)
      : Array.from(new Set([...prevAnswer.disabledSites, siteSlug]));
    setAnswer({ categories: selection.categories, enabledSites: sites, disabledSites: disabled });
    const outcome = await saveChange('/my-sites', siteSlug, enabled, { sites }, prevAnswer);
    if (outcome === "saved") {
      trackBackendEvent("protected_sites_changed", { change_type: "site", surface: "account" });
      if (getExtensionId) sendToExtension({ action: "categories-updated" }, getExtensionId).catch(() => {});
      return null;
    }
    const site = categories.flatMap(c => c.sites).find(s => s.slug === siteSlug);
    return failureMessage(outcome, site?.title);
  };

  // Clear all per-account state on logout / user-switch so it can't leak to the next user.
  const reset = () => {
    setBanks([]);
    banksRequested.current = false;
    setCustomSites([]);
    setMyCustomSites([]);
    setMyCustomSitesStatus("loading");
    setMyCustomSiteError(null);
    // ENG-3342: back to unknown, not to a default the next account could save over its own.
    setAnswer(null);
    setSelectionFailed(false);
    setSavedButNotReloaded(false);
    setOverrides(NO_OVERRIDES);
    setCustomSiteError(null);
    setExpandedCategory(null);
  };

  return {
    reset,
    banks,
    loadingBanks,
    customSites,
    loadingCustomSites,
    customSiteError,
    setCustomSiteError,
    sendToExtension,
    fetchBanks,
    loadBanksOnce,
    fetchCustomSites,
    handleRemoveCustomSite,
    myCustomSites,
    myCustomSitesStatus,
    addingMyCustomSite,
    myCustomSiteError,
    setMyCustomSiteError,
    fetchMyCustomSites,
    handleAddMyCustomSite,
    handleRemoveMyCustomSite,
    categories,
    trackedApps,
    verifiedRowsByCategory,
    defaultVerifiedRowsByCategory,
    loadingCategories,
    categoriesStatus,
    savedButNotReloaded,
    retryFailedLoads,
    enabledCategories,
    loadingEnabledCategories,
    expandedCategory,
    setExpandedCategory,
    enabledSites,
    /** ENG-4063: the per-site opt-outs as the server holds them; null until loaded. */
    disabledSites: answer?.disabledSites ?? null,
    setSiteEnabled,
    lockedCategories,
    lockedSites,
    fetchCategories,
    fetchEnabledCategories,
    toggleCategory,
    toggleSite,
    startCategoriesPolling,
    stopCategoriesPolling,
  };
}
